import { enrichEditorReactComponent } from '@wix/component-protocol/enricher';
import type {
  GetComponentResult,
  RuntimeEditorReactComponent,
} from '@wix/component-protocol/enricher';
import type { EditorReactComponent } from '@wix/component-protocol/schema';
import type { BuilderComponent } from '@wix/document-services-types';

import type { ManifestStore } from '../../stores';
import { ClientSpecMapService } from '../../sharedServices';
import {
  type ResolvedManifestCacheContext,
  ResolvedManifestPersistentCache,
} from './ResolvedManifestPersistentCache';

type CompleteResolvedManifest = (
  componentType: string,
  manifest: EditorReactComponent,
) => Promise<{
  manifest: EditorReactComponent;
  cacheable: boolean;
}>;

type EnrichOptions = {
  cacheContext?: ResolvedManifestCacheContext;
  completeManifest?: CompleteResolvedManifest;
};

export class EditorReactComponentEnricher {
  #isSpecEnabled: ((spec: string) => boolean) | undefined;

  constructor(
    private readonly manifestStore: ManifestStore,
    private readonly clientSpecMapService: ClientSpecMapService,
    private readonly persistentCache: ResolvedManifestPersistentCache,
    private readonly protocolVersion: string,
    isSpecEnabled?: (spec: string) => boolean,
  ) {
    this.#isSpecEnabled = isSpecEnabled;
  }

  async enrich(
    appDataComponent: BuilderComponent,
    compType: string,
    options: EnrichOptions = {},
  ): Promise<Record<string, EditorReactComponent>> {
    const cacheContext = options.cacheContext ?? {};
    const componentVersion =
      ResolvedManifestPersistentCache.extractComponentVersionToken(
        appDataComponent,
      );
    const cachedEntry = await this.persistentCache.getValidEntry(compType, {
      componentVersion,
      protocolVersion: this.protocolVersion,
      ...cacheContext,
    });

    if (cachedEntry) {
      return { [compType]: cachedEntry.resolvedManifest };
    }

    const { components, errors } = await enrichEditorReactComponent(
      appDataComponent as unknown as RuntimeEditorReactComponent,
      this.#getComponent.bind(this, cacheContext),
      this.#isSpecEnabled,
    );

    if (errors.length > 0) {
      console.error(
        `[EditorReactComponentEnricher] Enrichment errors for ${compType}:`,
        errors,
      );
    }

    const resolvedManifestEntries = await Promise.all(
      Object.entries(components).map(async ([enrichedCompType, component]) => {
        const enrichedManifest = component.data as EditorReactComponent;
        const enrichedComponentVersion =
          ResolvedManifestPersistentCache.extractComponentVersionToken(
            component,
          );
        const completed = options.completeManifest
          ? await options.completeManifest(enrichedCompType, enrichedManifest)
          : { manifest: enrichedManifest, cacheable: true };
        const resolvedManifest = completed.manifest;

        if (completed.cacheable) {
          await this.persistentCache.set(enrichedCompType, {
            resolvedManifest,
            componentVersion: enrichedComponentVersion,
            protocolVersion: this.protocolVersion,
            ...cacheContext,
          });
        }

        return [enrichedCompType, resolvedManifest] as const;
      }),
    );

    return Object.fromEntries(resolvedManifestEntries);
  }

  async #getComponent(
    cacheContext: ResolvedManifestCacheContext,
    compType: string,
  ): Promise<GetComponentResult | null> {
    const enrichedManifest = this.manifestStore.get(compType, {
      allowPending: true,
    })?.manifest;
    const appComponentData =
      this.clientSpecMapService.getAppComponentData(compType);

    if (enrichedManifest) {
      return {
        component: {
          ...(appComponentData ?? {}),
          data: enrichedManifest,
        } as unknown as RuntimeEditorReactComponent,
        resolved: true,
      };
    }

    if (!appComponentData?.data) {
      return null;
    }

    const componentVersion =
      ResolvedManifestPersistentCache.extractComponentVersionToken(
        appComponentData,
      );
    const cachedEntry = await this.persistentCache.getValidEntry(compType, {
      componentVersion,
      protocolVersion: this.protocolVersion,
      ...cacheContext,
    });

    if (cachedEntry) {
      return {
        component: {
          ...appComponentData,
          data: cachedEntry.resolvedManifest,
        } as unknown as RuntimeEditorReactComponent,
        resolved: true,
      };
    }

    return {
      component: appComponentData as unknown as RuntimeEditorReactComponent,
      resolved: false,
    };
  }
}
